/**
 * Safe Access Utility Functions
 * Provides defensive property and array access with fallback values
 */

/**
 * Safely access array element at specified index
 * @param array - The array to access
 * @param index - The index to access
 * @param defaultValue - The default value to return if array or element is invalid
 * @returns The array element at the index or the default value
 */
export function safeArrayAccess<T>(
  array: T[] | null | undefined,
  index: number,
  defaultValue: T | null = null
): T | null {
  if (!Array.isArray(array)) {
    return defaultValue;
  }
  if (index < 0 || index >= array.length) {
    return defaultValue;
  }
  return array[index] ?? defaultValue;
}

/**
 * Safely check if array has minimum required length
 * @param array - The array to check
 * @param requiredLength - The minimum required length
 * @returns true if array exists and has at least requiredLength elements
 */
export function hasArrayMinLength(
  array: any[] | null | undefined,
  requiredLength: number
): boolean {
  return Array.isArray(array) && array.length >= requiredLength;
}

/**
 * Safely access nested property using dot notation
 * @param obj - The object to access
 * @param path - The property path (e.g., 'user.profile.name')
 * @param defaultValue - The default value to return if property doesn't exist
 * @returns The property value or the default value
 */
export function safePropertyAccess(
  obj: any,
  path: string,
  defaultValue: any = null
): any {
  if (!obj || typeof obj !== 'object') {
    return defaultValue;
  }

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue;
    }
  }

  return current ?? defaultValue;
}

/**
 * Safely validate and extract array slices
 * @param array - The array to slice
 * @param start - The start index
 * @param end - The end index
 * @returns The sliced array or empty array
 */
export function safeArraySlice<T>(
  array: T[] | null | undefined,
  start: number = 0,
  end?: number
): T[] {
  if (!Array.isArray(array)) {
    return [];
  }
  return array.slice(start, end) || [];
}

/**
 * Safely filter and validate array
 * @param array - The array to filter
 * @param predicate - The filter predicate function
 * @returns The filtered array or empty array
 */
export function safeArrayFilter<T>(
  array: T[] | null | undefined,
  predicate: (item: T) => boolean
): T[] {
  if (!Array.isArray(array)) {
    return [];
  }
  try {
    return array.filter(predicate) || [];
  } catch (error) {
    console.error('Error filtering array:', error);
    return [];
  }
}